import {ArrowBackIos, ArrowForwardIos} from '@mui/icons-material';
import {Typography} from '@mui/material';
import React, {useState} from 'react';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {postTaxProfileReviewStatus} from 'src/appApi';
import {useActiveYear} from 'src/common/hooks/useActiveYear';
import {
  TaxProfileSection,
  TaxProfileSectionToSubsectionMapping,
  themmeColor,
} from 'src/constants/constants';
import useCpaCenterV2Navigation from 'src/CpaCenterV2/hooks/useCpaCenterV2Navigation';
import useCurrentUserId from 'src/CpaCenterV2/hooks/useCurrentUserId';
import useLayout from 'src/CpaCenterV2/hooks/useLayout';
import useTaxProfileReview from 'src/CpaCenterV2/hooks/useTaxProfileReview';
import DSButton from 'src/DesignSystem/Button/Button';
import useNotify from 'src/DesignSystem/Notify/useNotify';
import BackIcon from 'src/icons/BackIcon';
import TickIcon from 'src/icons/TickIcon';
import {NotificationType} from 'src/store/app/app.reducer';
import {fetchTaxProfileStatusForGivenYear} from 'src/store/taxProfileStatus/taxProfileStatus.actions';
import {TaxProfileReviewStatus} from 'src/store/taxProfileStatus/taxProfileStatus.reducer';

interface SectionHeaderProps {
  sectionName: string;
  section: TaxProfileSection;
}

const SectionHeader = ({sectionName, section}: SectionHeaderProps) => {
  const {activeYear} = useActiveYear();
  const {userId} = useCurrentUserId();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {notify} = useNotify();
  const {loaded, isSectionReviewPending, getNextSection, getPreviousSection} =
    useTaxProfileReview();
  const {navigateToTaxProfileSection, navigateToTaxProfileReview} =
    useCpaCenterV2Navigation();
  const [isLoading, setIsLoading] = useState(false);
  const onClickBack = () => {
    // const route = CpaCenterV2Route.CpaCenterV2TaxProfileReview.replace(
    //   CpaCenterV2RouteParts.UserId,
    //   `${userId}`,
    // );
    // navigate(route);
    navigateToTaxProfileReview();
  };

  const allSubsections = TaxProfileSectionToSubsectionMapping[section];

  const isAnyPending = isSectionReviewPending(section);

  const onClickReviewAll = async () => {
    try {
      setIsLoading(true);
      const completedSectionsBody = {};
      allSubsections.forEach((subsection) => {
        completedSectionsBody[subsection] = TaxProfileReviewStatus.COMPLETE;
      });
      await postTaxProfileReviewStatus(
        userId,
        activeYear,
        completedSectionsBody,
      );
      await dispatch(fetchTaxProfileStatusForGivenYear(userId, activeYear));
      notify(`Successfully reviewed ${sectionName}`, NotificationType.success);
    } catch (e) {
      notify(
        `Error while marking step as reviewed ${e}`,
        NotificationType.error,
      );
    } finally {
      setIsLoading(false);
    }
  };
  const nextSection = getNextSection(section);
  const previousSection = getPreviousSection(section);
  const {TAX_PROFILE_SECTION_HEADER_HEIGHT} = useLayout();

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingBlock: 12,
        paddingRight: 12,
        height: TAX_PROFILE_SECTION_HEADER_HEIGHT,
      }}>
      <div style={{display: 'flex', alignItems: 'center'}}>
        <div
          onClick={onClickBack}
          style={{
            backgroundColor: themmeColor.black,
            width: 36,
            height: 36,
            borderRadius: 100,
            justifyContent: 'center',
            alignItems: 'center',
            display: 'flex',
          }}>
          <BackIcon color={themmeColor.white} />
        </div>
        <Typography style={{marginLeft: 12, fontWeight: 800, fontSize: 20}}>
          {sectionName}
        </Typography>
        {previousSection && (
          <DSButton
            startIcon={<ArrowBackIos />}
            text={`${previousSection.stepName}`}
            type={'secondary'}
            style={{marginLeft: 8}}
            onClick={() =>
              navigateToTaxProfileSection(previousSection.section)
            }
          />
        )}
        {nextSection && (
          <DSButton
            endIcon={<ArrowForwardIos />}
            text={`${nextSection.stepName}`}
            type={'secondary'}
            style={{marginLeft: 8}}
            onClick={() => navigateToTaxProfileSection(nextSection.section)}
          />
        )}
      </div>
      <div style={{display: 'flex'}}>
        {isAnyPending ? (
          <DSButton
            type="primary"
            onClick={onClickReviewAll}
            text="Reviewed all"
            disabled={!loaded || isLoading}
          />
        ) : (
          <div style={{display: 'flex', alignItems: 'center'}}>
            <TickIcon />
          </div>
        )}
      </div>
    </div>
  );
};

export default SectionHeader;
